import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useUser } from '../contexts/UserContext';
import { courtApi } from '../services/api';
import html2canvas from 'html2canvas';

const Receipt = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { refreshUserProfile } = useUser();
  const { paymentId } = useParams();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const receiptRef = useRef(null);

  useEffect(() => {
    if (paymentId) {
      fetchReceiptData();
    } else {
      setError('No payment ID provided');
      setLoading(false);
    }
  }, [paymentId]);

  const fetchReceiptData = async () => {
    try {
      setLoading(true);
      const result = await courtApi.getPayment(paymentId);
      setPaymentData(result);
      
      // Refresh user profile to update points after successful payment
      if (result?.payment?.status === 'completed') {
        await refreshUserProfile();
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching receipt:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const locale = t('language') === 'en' ? 'en-US' : 'th-TH';
    return date.toLocaleDateString(locale, { 
      year: 'numeric', 
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDuration = (minutes) => {
    const hours = minutes / 60;
    return hours === 1 ? `1 ${t('hour')}` : `${hours} ${t('hours')}`;
  };

  const handleSaveReceipt = async () => {
    if (!receiptRef.current) return;
    
    try {
      setSaving(true);
      
      // Capture the receipt as canvas
      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: '#10b981', // Match the gradient background
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: receiptRef.current.offsetWidth,
        height: receiptRef.current.offsetHeight,
      });
      
      // Convert canvas to data URL
      const dataUrl = canvas.toDataURL('image/png', 0.9);
      
      // Try Web Share API first (works in LINE webview)
      if (navigator.share) {
        try {
          // Convert data URL to blob for sharing
          const response = await fetch(dataUrl);
          const blob = await response.blob();
          const file = new File([blob], `receipt-${paymentData?.booking?.id || 'booking'}.png`, { type: 'image/png' });
          
          await navigator.share({
            title: t('booking_confirmed'),
            text: t('receipt_share_text'),
            files: [file]
          });
          
          alert(t('receipt_shared'));
          return;
        } catch (shareError) {
          console.log('Web Share API failed, falling back to download');
        }
      }
      
      // Fallback: Create a download link
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `receipt-${paymentData?.booking?.id || 'booking'}.png`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show success message
      alert(t('receipt_saved'));
      
    } catch (error) {
      console.error('Error saving receipt:', error);
      alert(t('save_receipt_error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-40 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-5">
        <div className="flex flex-col items-center justify-center h-screen text-white">
          <div className="w-12 h-12 border-4 border-white border-opacity-30 border-t-white rounded-full animate-spin mb-5"></div>
          <p className="text-xl">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-40 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-5">
        <div className="flex flex-col items-center justify-center h-screen text-white text-center">
          <div className="bg-red-500 rounded-full p-4 mb-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-4">{t('receipt_error')}</h2>
          <p className="text-lg mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-white bg-opacity-20 border-2 border-white text-white px-6 py-3 rounded-full text-lg font-medium transition-all duration-300 hover:bg-white hover:text-green-500"
          >
            {t('back_to_home')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-40 bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500 p-5">
      <div className="max-w-md mx-auto">
        {/* Receipt Card */}
        <div ref={receiptRef} className="bg-white rounded-3xl p-8 shadow-2xl">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">{t('booking_confirmed')}</h1>
          </div>

          {/* Receipt Content */}
          <div className="space-y-4 text-center">
            {/* Thank you message */}
            <p className="text-lg text-gray-700">
              {t('thank_you')} {paymentData?.booking?.displayName}
            </p>

            {/* Date */}
            <p className="text-lg text-gray-700">
              {t('see_you_on')} {formatDate(paymentData?.booking?.date)}
            </p>

            {/* Time */}
            <p className="text-lg text-gray-700">
              {paymentData?.booking?.startTime} - {paymentData?.booking?.endTime}
            </p>

            {/* Duration */}
            <p className="text-lg text-gray-700">
              {formatDuration(paymentData?.booking?.duration)}
            </p>

            {/* Court name */}
            <p className="text-lg font-semibold text-gray-800">
              {paymentData?.court?.name}
            </p>

            {/* Booking ID */}
            <p className="text-sm text-gray-600">
              {t('booking_reference')}: {paymentData?.booking?.id}
            </p>

            {/* Contact Information */}
            {(paymentData?.booking?.contactData?.nickname || paymentData?.booking?.contactData?.mobilePhone) && (
              <div className="mt-4 p-4 bg-indigo-50 rounded-2xl">
                <p className="text-sm text-indigo-800 font-medium mb-2">
                  {t('contact_details')}
                </p>
                <div className="text-xs text-indigo-600 space-y-1">
                  {paymentData?.booking?.contactData?.nickname && (
                    <p>{t('nickname')}: {paymentData.booking.contactData.nickname}</p>
                  )}
                  {paymentData?.booking?.contactData?.mobilePhone && (
                    <p>{t('mobile_phone')}: {paymentData.booking.contactData.mobilePhone}</p>
                  )}
                </div>
              </div>
            )}

            {/* Tokens payment info */}
            {paymentData?.payment?.paymentMethod === 'tokens' && (
              <div className="mt-4 p-4 bg-purple-50 rounded-2xl">
                <p className="text-sm text-purple-800 font-medium mb-2">
                  {t('tokens_payment') || 'Tokens Payment'}
                </p>
                <div className="text-xs text-purple-600 space-y-1">
                  <p>{t('tokens_used') || 'Tokens Used'}: {paymentData?.payment?.tokensUsed || 0}</p>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="mt-6 p-4 bg-blue-50 rounded-2xl">
              <p className="text-sm text-blue-800 font-medium">
                {t('show_this_page')}
              </p>
            </div>

            {/* Contact info */}
            <div className="mt-6 p-4 bg-gray-50 rounded-2xl">
              <p className="text-sm text-gray-600 mb-2">
                {t('contact_admin')}
              </p>
              <div className="text-xs text-gray-500 space-y-1">
                <p>{t('weekday_hours')}</p>
                <p>{t('weekend_hours')}</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 space-y-3">
            <button
              onClick={handleSaveReceipt}
              disabled={saving}
              className="w-full bg-blue-500 text-white py-4 rounded-2xl font-semibold text-lg transition-all duration-200 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="w-6 h-6 border-2 border-white border-opacity-30 border-t-white rounded-full animate-spin"></div>
                  <span>{t('saving')}</span>
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{t('share_receipt')}</span>
                </>
              )}
            </button>
            
            <button
              onClick={() => navigate('/')}
              className="w-full bg-green-500 text-white py-4 rounded-2xl font-semibold text-lg transition-all duration-200 hover:bg-green-600"
            >
              {t('back_to_home')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Receipt; 